'use client';

import * as React from 'react';

import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';

import { cn } from '@avodah-inc/ui/utils';

import { Label } from '..';

type RadioGroupItemProps = React.ComponentPropsWithoutRef<
  typeof RadioGroupPrimitive.Item
>;

const RadioGroupItem = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Item>,
  RadioGroupItemProps
>(({ className, ...props }, ref) => {
  return (
    <RadioGroupPrimitive.Item
      className={cn(
        'focus-visible:ring-ring border-primary-500 text-primary-500 ring-offset-primary hover:border-primary-600 hover:text-primary-600 peer aspect-square h-4 w-4 rounded-full border transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:border-gray-400 disabled:text-gray-400',
        className,
      )}
      ref={ref}
      {...props}
    >
      <RadioGroupPrimitive.Indicator className="flex items-center justify-center">
        <span className="h-2 w-2 rounded-full bg-current" />
      </RadioGroupPrimitive.Indicator>
    </RadioGroupPrimitive.Item>
  );
});

RadioGroupItem.displayName = RadioGroupPrimitive.Item.displayName;

export type RadioGroupProps = {
  options: {
    label: string;
    value: string;
  }[];
  dataTestIdMap?: Record<string, string>;
} & React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root>;

export const RadioGroup = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Root>,
  RadioGroupProps
>(({ className, options = [], ...props }, ref) => {
  return (
    <RadioGroupPrimitive.Root
      className={cn('flex items-center gap-10', className)}
      {...props}
      ref={ref}
    >
      {options.map((option) => {
        return (
          <div className="flex items-center space-x-3" key={option.value}>
            <RadioGroupItem
              id={option.value}
              value={option.value}
              data-testid={props.dataTestIdMap?.[option.value]}
            />
            <Label htmlFor={option.value}>{option.label}</Label>
          </div>
        );
      })}
    </RadioGroupPrimitive.Root>
  );
});

RadioGroup.displayName = RadioGroupPrimitive.Item.displayName;
